import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { store } from 'react-notifications-component';
import { withRouter, useLocation } from 'react-router-dom';
import {
  Utensils,
  Calendar,
  Soup,
  ClipboardList,
  Truck,
  Bike,
  Smile,
  Dumbbell,
  Leaf,
  Heart,
  Star,
  ArrowRight,
  ArrowLeft,
  Quote,
  Gift,
  PauseCircle,
  XCircle,
} from 'lucide-react';

import LoadingAnimation from 'components/common/LoadingAnimation';
import { loadPresetList } from 'redux/actions/presetActions';
import initialState from 'redux/reducers/initialState';
import { signinRequest } from 'redux/actions/authActions';
import { authenticateWithToken } from 'api/authApi';

import './index.scss';

const queryString = require('query-string');

const FEATURES = [
  {
    Icon: Gift,
    title: 'No Minimum Order',
    text: 'Order for any team size.',
  },
  {
    Icon: Truck,
    title: 'Free Delivery',
    text: 'On all subscriptions.',
  },
  {
    Icon: PauseCircle,
    title: 'Pause Anytime',
    text: 'Flexible and hassle-free.',
  },
  {
    Icon: XCircle,
    title: 'Cancel Anytime',
    text: 'No long-term contracts.',
  },
];

const PARTNER_LOGOS = [
  {
    src: 'https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/microsoft.svg',
    alt: 'Microsoft',
  },
  { src: '/assets/img/home/shopify-logo.svg', alt: 'Shopify' },
  { src: '/assets/img/home/spotify.svg', alt: 'Spotify' },
  {
    src: 'https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/invision.svg',
    alt: 'InVision',
  },
  {
    src: 'https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/slack.svg',
    alt: 'Slack',
  },
];

const WHY_LOVE = [
  {
    iconImg: '/assets/img/about/01.png',
    title: 'Delicious & Healthy',
    text: 'Made satisfyingly good meals with fresh, quality ingredients.',
  },
  {
    iconImg: '/assets/img/about/02.png',
    title: 'On-time Delivery',
    text: 'We deliver on time, every time, right to your office.',
  },
  {
    iconImg: '/assets/img/about/03.png',
    title: 'Easy & Flexible',
    text: 'Flexible plans, easy ordering, and quick changes.',
  },
  {
    iconImg: '/assets/img/about/04.png',
    title: 'Loved by Employees',
    text: 'Make your team excited to eat, every single day.',
  },
];

const STEPS = [
  {
    iconImg: '/assets/img/about/17.png',
    step: 1,
    title: 'Choose Your Plan',
    text: 'Pick the plan that fits your team size and preferences.',
  },
  {
    iconImg: '/assets/img/about/18.png',
    step: 2,
    title: 'We Prepare',
    text: 'Our chefs prepare fresh, delicious meals just for your team.',
  },
  {
    iconImg: '/assets/img/about/19.png',
    step: 3,
    title: 'We Deliver',
    text: 'Meals are delivered on time, right to your office.',
  },
  {
    iconImg: '/assets/img/about/20.png',
    step: 4,
    title: 'Your Team Enjoys',
    text: 'Happy, energized employees and a better lunch experience.',
  },
];

const MEALS = [
  {
    img: '/assets/img/panda/hero-food-bowl.png',
    title: 'Mediterranean Power Bowl',
    tags: ['High Protein', 'GF'],
    TagIcon: Dumbbell,
  },
  {
    img: '/assets/img/panda/teriyaki-chicken-bowl.jpg',
    title: 'Teriyaki Chicken',
    tags: ['Balanced', 'Healthy'],
    TagIcon: Heart,
  },
  {
    img: '/assets/img/panda/mediterranean-quinoa-salad.jpg',
    title: 'Quinoa & Chickpea Salad',
    tags: ['Vegan', 'GF'],
    TagIcon: Leaf,
  },
  {
    img: '/assets/img/panda/tomato-spaghetti.jpg',
    title: 'Classic Pasta',
    tags: ['Comfort', 'Bestseller'],
    TagIcon: Star,
  },
];

const TESTIMONIALS = [
  {
    img: '/assets/img/about/avatar_ayesha.jpg',
    name: 'Ayesha Khan',
    role: 'HR Manager, TechNova',
    text:
      'PandaOnWheels has completely transformed our team lunches. The food is amazing, delivery is always on time, and our team loves the variety!',
    stars: 5,
  },
  {
    img: '/assets/img/about/avatar_usman.jpg',
    name: 'Usman Ali',
    role: 'Operations Lead, FinEdge',
    text:
      'Ordering is effortless and the quality is consistently great. It has become a highlight of our workweek.',
    stars: 5,
  },
  {
    img: '/assets/img/about/avatar_sara.jpg',
    name: 'Sara Malik',
    role: 'Office Manager, CreativeLabs',
    text:
      'Reliable, flexible and professional. The best catering partner we have worked with for corporate office meals.',
    stars: 5,
  },
];

const HomePage = ({ history }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const presets = useSelector(state => state.presets);
  const { search } = useLocation();

  const { confirmed, name, token } = queryString.parse(search);

  async function fetchUserData(authToken) {
    try {
      const auth = await authenticateWithToken({
        Authorization: `Bearer ${authToken}`,
      });
      if (auth && auth.user && auth.user.confirmed) {
        dispatch(signinRequest(auth));
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }
    } catch (e) {
      console.log('error', e);
    }
  }

  useEffect(() => {
    if (confirmed === 'ok') {
      store.addNotification({
        title: `Hello ${name}, good news!`,
        message: 'Your account has been verified. Please log in.',
        type: 'success',
        insert: 'top',
        container: 'top-center',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        width: 400,
        dismiss: {
          duration: 10000,
          showIcon: true,
        },
      });
    } else if (confirmed === 'failed') {
      store.addNotification({
        title: 'Hmm ...',
        message:
          "Your account verification didn't work. Please contact us at support@pandaonwheels.com and we're Happy to help!",
        type: 'danger',
        insert: 'top',
        container: 'top-center',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        width: 500,
        dismiss: {
          duration: 10000,
          showIcon: true,
        },
      });
    }
  }, [confirmed]);

  useEffect(() => {
    if (token) {
      fetchUserData(token);
    }
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (presets === initialState.presets) {
          await dispatch(loadPresetList());
        }
      } catch (e) {
        console.log('error', e);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const goToMenu = () => history.push('/browse-restaurants');

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <main className="pow-home">
      {/* HERO */}
      <section className="pow-hero">
        <div className="pow-hero__grid">
          <div className="pow-hero__content">
              <span className="pow-badge pow-badge--plain">EMPLOYEE LUNCH, DELIVERED</span>
              <h1 className="pow-hero__title">
                Great lunches.
                <br />
                <span className="pow-text-red">Happier teams.</span>
              </h1>
              <p className="pow-hero__subtitle">
                Delicious, healthy meals delivered to your office. Make lunch
                the best part of the workday.
              </p>

              <div className="pow-partners">
                <span className="pow-partners__label">Loved by teams at</span>
                <div className="pow-partners__logos">
                  {PARTNER_LOGOS.map(logo => (
                    <span className="pow-partners__item" key={logo.alt}>
                      <img src={logo.src} alt={logo.alt} />
                      <span>{logo.alt}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="pow-hero__media">
              <img
                src="/assets/img/panda/panda-scooter.png"
                alt="PandaOnWheels delivery mascot"
              />
            </div>
          </div>
      </section>

      {/* WHY TEAMS LOVE US */}
      <section className="pow-section">
        <div className="container-fluid">
          <h2 className="pow-section__title">
            Why teams love{' '}
            <span className="pow-text-red">PandaOnWheels</span>
          </h2>
          <div className="pow-why">
            {WHY_LOVE.map(({ iconImg, title, text }) => (
              <div className="pow-why__item" key={title}>
                <div className="pow-why__icon">
                  <img src={iconImg} alt={title} className="pow-step-custom-icon" />
                </div>
                <h4>{title}</h4>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="pow-section pow-section--alt">
        <div className="container-fluid">
          <h2 className="pow-section__title">How it works</h2>
          <div className="pow-steps">
            {STEPS.map(({ iconImg, step, title, text }) => (
              <div className="pow-steps__item" key={title}>
                <div className="pow-steps__top">
                  <div className="pow-steps__icon pow-steps__icon--img">
                    <img src={iconImg} alt={`Step ${step}: ${title}`} className="pow-step-badge-img" />
                  </div>
                </div>
                <h4>{title}</h4>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEALS FOR EVERY TASTE */}
      <section className="pow-section pow-section--picks">
        <div className="container-fluid">
          <div className="pow-picks">
            <div className="pow-picks__intro">
              <h2 className="pow-picks__title">
                Meals for every{' '}
                <span className="pow-text-red">taste &amp; lifestyle</span>
              </h2>
              <p className="pow-picks__subtitle">
                From hearty classics to plant-based options, we&apos;ve got
                something everyone will love.
              </p>
              <ul className="pow-picks__list">
                <li>Vegetarian &amp; Vegan Options</li>
                <li>Gluten-Free Choices</li>
                <li>Low-Carb &amp; Keto Friendly</li>
                <li>Seasonal &amp; Local Ingredients</li>
              </ul>
              <button
                type="button"
                className="pow-btn pow-btn--green"
                onClick={goToMenu}
              >
                Browse Restaurants <ArrowRight size={18} />
              </button>
            </div>

            <div className="pow-picks__cards">
              {MEALS.map(({ img, title, tags, TagIcon }) => (
                <div className="pow-card" key={title}>
                  <div className="pow-card__img">
                    <img src={img} alt={title} />
                  </div>
                  <div className="pow-card__body">
                    <h5>{title}</h5>
                    <span className="pow-card__tag">
                      <TagIcon size={14} /> {tags[0]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pow-dots">
            {TESTIMONIALS.map((_, d) => (
              <button
                type="button"
                key={d}
                aria-label={`slide ${d + 1}`}
                className={`pow-dots__dot ${activeTestimonial === d ? 'is-active' : ''}`}
                onClick={() => setActiveTestimonial(d)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="pow-section pow-section--alt">
        <div className="container-fluid">
          <div className="pow-testimonials">
            <div className="pow-testimonials__nav">
              <button
                type="button"
                aria-label="previous"
                onClick={() =>
                  setActiveTestimonial(prev =>
                    prev === 0 ? TESTIMONIALS.length - 1 : prev - 1,
                  )
                }
              >
                <ArrowLeft size={20} />
              </button>
            </div>

            <div className="pow-testimonials__content">
              <div className="pow-testimonials__quote">
                <Quote className="pow-testimonials__quote-icon" size={44} fill="currentColor" />
                <p className="pow-testimonials__text">
                  {TESTIMONIALS[activeTestimonial].text}
                </p>
              </div>
              <div className="pow-testimonials__author">
                <img
                  src={TESTIMONIALS[activeTestimonial].img}
                  alt={TESTIMONIALS[activeTestimonial].name}
                />
                <strong>{TESTIMONIALS[activeTestimonial].name}</strong>
                <span>{TESTIMONIALS[activeTestimonial].role}</span>
                <div className="pow-testimonials__stars">
                  {Array.from({ length: TESTIMONIALS[activeTestimonial].stars }).map((_, i) => (
                    <Star key={i} size={16} fill="currentColor" />
                  ))}
                </div>
              </div>
            </div>

            <div className="pow-testimonials__nav">
              <button
                type="button"
                aria-label="next"
                onClick={() =>
                  setActiveTestimonial(prev =>
                    prev === TESTIMONIALS.length - 1 ? 0 : prev + 1,
                  )
                }
              >
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pow-section pow-section--cta">
        <div className="container-fluid">
          <div className="pow-catering">
            <div className="pow-catering__content">
              <span className="pow-catering__badge">READY TO GET STARTED?</span>
              <h2>Bring joy back to lunch.</h2>
              <p>
                Join 100+ companies that trust PandaOnWheels for happier,
                healthier teams.
              </p>
              <button
                type="button"
                className="pow-btn pow-btn--red"
                onClick={goToMenu}
              >
                Get Started
              </button>
            </div>

            <div className="pow-catering__media">
              <img
                src="/assets/img/panda/carryout-lunchbox.png"
                alt="PandaOnWheels lunch box"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="pow-section pow-section--features">
        <div className="container-fluid">
          <div className="pow-features">
            {FEATURES.map(({ Icon, title, text }) => (
              <div className="pow-features__item" key={title}>
                <div className="pow-features__icon">
                  <Icon size={24} />
                </div>
                <div>
                  <h4>{title}</h4>
                  <p>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

HomePage.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export default withRouter(HomePage);
